import axios from 'axios';
import { ApiErrorResponse } from '../types/ApiErrorResponse';

/**
 * Creates an ApiErrorResponse from an axios error.
 * Handles network errors (no response), server errors (500), and structured API errors.
 * @param error - The caught error (typically from axios)
 * @param fallbackMessage - Context-specific fallback message
 * @returns ApiErrorResponse suitable for rejectWithValue
 */
export const toApiError = (
  error: unknown,
  fallbackMessage: string = "An unexpected error occurred. Please try again."
): ApiErrorResponse => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const data = error.response?.data;

    // Try to extract message from structured API response
    const message =
      (typeof data === 'object' && data !== null && typeof data.message === 'string' && data.message) ||
      (typeof data === 'object' && data !== null && typeof data.title === 'string' && data.title) ||
      (status ? `Server error (${status})` : fallbackMessage);

    return {
      message,
      statusCode: status,
      errors: typeof data === 'object' && data !== null ? data.errors : undefined,
    };
  }

  return {
    message: fallbackMessage,
  };
};

/**
 * Extracts user-friendly error message from API error payload
 * @param errorPayload - Error payload from API response (ApiErrorResponse or legacy format)
 * @param defaultMessage - Default message to use if extraction fails
 * @returns User-friendly error message
 */
export const extractApiErrorMessage = (
  errorPayload: any, 
  defaultMessage: string = "An unexpected error occurred. Please try again."
): string => {
  if (!errorPayload || typeof errorPayload !== 'object') {
    return defaultMessage;
  }

  // Check for direct message (ApiErrorResponse format)
  if ('message' in errorPayload && typeof errorPayload.message === 'string') {
    return errorPayload.message;
  }

  // Check for validation errors (ApiErrorResponse format)
  if ('errors' in errorPayload && errorPayload.errors) {
    const apiErrors = errorPayload.errors;
    if (typeof apiErrors === 'object') {
      const firstError = Object.values(apiErrors)[0];
      if (Array.isArray(firstError) && firstError.length > 0) {
        return firstError[0];
      } else if (typeof firstError === 'string') {
        return firstError;
      }
    }
  }

  return defaultMessage;
};

/**
 * Extracts user-friendly error message from typed API error response
 * @param error - ApiErrorResponse object
 * @param defaultMessage - Default message to use if extraction fails
 * @returns User-friendly error message
 */
export const extractTypedApiErrorMessage = (
  error: ApiErrorResponse | undefined,
  defaultMessage: string = "An unexpected error occurred. Please try again."
): string => {
  if (!error) {
    return defaultMessage;
  }

  // Return the message directly from ApiErrorResponse
  if (error.message) {
    return error.message;
  }

  // Check for validation errors
  if (error.errors) {
    const firstError = Object.values(error.errors)[0];
    if (Array.isArray(firstError) && firstError.length > 0) {
      return firstError[0];
    } else if (typeof firstError === 'string') {
      return firstError;
    }
  }

  return defaultMessage;
};
